import React from "react"
import { useSelector, useDispatch } from "react-redux"

import Images from "assets/images"
import {
    VIEW, DELETE, EDIT,
    COMPLIANCE, REVIEW,
    PAYMENT_CURRENCY,
} from "const"
import { CcLibrarySet } from "server/models"
import { hasAccess, hasProjectAccess } from "client/utils"
import {
    PRM_CC_LIBRARY,
    StoreType,
    LangType,

    IsOwnerCcLibrarySet,
    addModal,
} from "client/store"
import {
    Link,
    Logo,
    UserIcon,
    DateFormatter,
    TableColumn,
} from "client/components"

import ModuleCheckbox from "./moduleCheckbox"

const Columns = () => {
    const baseRoute = useSelector((state: StoreType) => state.ccLibrarySet.baseRoute)

    const project = useSelector((state: StoreType) => state.project.projectView)
    const userList = useSelector((state: StoreType) => state.user.userList)
    const accountList = useSelector((state: StoreType) => state.account.accountList)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const setLink = (set: CcLibrarySet, linkContent: any) => {
        const href = `${baseRoute}/${VIEW}/${set.id}`
        return (
            <Link
                href={href}
                onClick={() => dispatch(addModal({ url: href }))}
            >
                {linkContent}
            </Link>
        )
    }

    const editAccess = !project.id
        ? hasAccess(PRM_CC_LIBRARY, EDIT)
        : hasProjectAccess(PRM_CC_LIBRARY, EDIT)

    const deleteAccess = !project.id
        ? hasAccess(PRM_CC_LIBRARY, DELETE)
        : hasProjectAccess(PRM_CC_LIBRARY, DELETE)

    const columns: TableColumn[] = [
        {
            field: "id",
            header: lang.id,
            sortable: true,
        },
        {
            field: "logo",
            header: lang.logo,
            centerAlign: true,
            formatter: (set: CcLibrarySet) => (
                setLink(set, <Logo table logo={set.logo || Images.complianceSetLogo} />)
            ),
        },
        {
            field: "publicId",
            header: lang.publicId,
            sortable: true,
            cellMinWidth: "150px",
            cellMaxWidth: "300px",
            formatter: (set: CcLibrarySet) => setLink(set, set.publicId),
        },
        {
            field: "name",
            header: lang.name,
            sortable: true,
            cellMinWidth: "200px",
            cellMaxWidth: "400px",
            formatter: (set: CcLibrarySet) => setLink(set, set.name),
        },
    ]

    if (userData.accountId === 1) {
        columns.push(
            {
                field: "owner",
                header: lang.owner,
                cellMinWidth: "150px",
                formatter: (set: CcLibrarySet) => {
                    const account = accountList[set.ownerAccount]
                    const user = userList[set.creator]

                    if (project.id) return account.name
                    return (set.ownerAccount === userData.accountId)
                        ? <UserIcon user={user} size="sm" />
                        : account.name
                },
            },
            {
                field: "locked",
                header: lang.locked,
                sortable: true,
                cellWidth: "100px",
                cellMinWidth: "100px",
                formatter: (set: CcLibrarySet) => (set.locked ? lang.yes : lang.no),
            },
            {
                field: "formal",
                header: lang.formal,
                sortable: true,
                cellWidth: "100px",
                cellMinWidth: "100px",
                formatter: (set: CcLibrarySet) => (set.formal ? lang.yes : lang.no),
            },
        )
    }

    if (!project.id) {
        columns.push({
            field: "default",
            header: lang.default,
            sortable: true,
            cellWidth: "100px",
            cellMinWidth: "100px",
            formatter: (set: CcLibrarySet) => (set.default ? lang.yes : lang.no),
        })

        if (userData.accountId === 1) {
            columns.push(
                {
                    field: "access",
                    header: lang.access,
                    sortable: true,
                    cellWidth: "100px",
                    cellMinWidth: "100px",
                    formatter: (set: CcLibrarySet) => lang[set.access as keyof LangType],
                },
                {
                    field: "cost",
                    header: lang.cost,
                    sortable: true,
                    cellWidth: "100px",
                    cellMinWidth: "100px",
                    formatter: (set: CcLibrarySet) => (
                        set.cost === 0
                            ? lang.free
                            : <span style={{ textTransform: "uppercase" }}>{`${set.cost.toFixed(2)} ${PAYMENT_CURRENCY}`}</span>
                    ),
                },
            )
        }
        columns.push(
            {
                field: "status",
                header: lang.status,
                centerAlign: true,
                sortable: true,
                editAccess: (set: CcLibrarySet) => editAccess || IsOwnerCcLibrarySet(project.id, set, userData),
            },
        )
    }

    if (project.id) {
        columns.push(
            {
                field: "compliance",
                header: lang.compliance,
                cellWidth: "100px",
                cellMinWidth: "100px",
                centerAlign: true,
                formatter: (set: CcLibrarySet) => <ModuleCheckbox set={set} module={COMPLIANCE} />,
            },
            {
                field: "review",
                header: lang.review,
                cellWidth: "100px",
                cellMinWidth: "100px",
                centerAlign: true,
                formatter: (set: CcLibrarySet) => <ModuleCheckbox set={set} module={REVIEW} />,
            },
        )
    }

    columns.push(
        {
            field: "createdAt",
            header: lang.created,
            sortable: true,
            formatter: (set: CcLibrarySet) => <DateFormatter date={set.createdAt} />,
        },
        {
            field: "delete",
            centerAlign: true,
            deleteAccess: (set: CcLibrarySet) => deleteAccess || IsOwnerCcLibrarySet(project.id, set, userData),
        },
    )

    return columns
}
export default Columns
